/**
 * TradeStack - Dashboard Page
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useConfig } from '../context/ConfigContext';
import api from '../api';
import './Dashboard.css';

function Dashboard() {
  const { formatCurrency } = useConfig();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const response = await api.getDashboard();
      setStats(response.data);
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-page">
        <div className="loading-state">Loading dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-page">
        <div className="error-state">{error}</div>
      </div>
    );
  }

  const data = stats || {
    thisMonth: { revenue: 0, expenses: 0 },
    outstanding: { amount: 0, count: 0 },
    activeJobs: 0,
    unreviewedReceipts: 0,
    yearToDate: { revenue: 0, expenses: 0, profit: 0 }
  };

  return (
    <div className="dashboard-page">
      <div className="page-header">
        <h1>Dashboard</h1>
        <div className="header-actions">
          <Link to="/jobs/new" className="btn btn-primary">+ New Job</Link>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon revenue-icon">$</div>
          <div className="stat-content">
            <span className="stat-label">This Month</span>
            <span className="stat-value">{formatCurrency(data.thisMonth.revenue)}</span>
            <span className="stat-sub">Revenue</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon expenses-icon">-</div>
          <div className="stat-content">
            <span className="stat-label">This Month</span>
            <span className="stat-value">{formatCurrency(data.thisMonth.expenses)}</span>
            <span className="stat-sub">Expenses</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon outstanding-icon">!</div>
          <div className="stat-content">
            <span className="stat-label">Outstanding</span>
            <span className="stat-value">{formatCurrency(data.outstanding.amount)}</span>
            <span className="stat-sub">{data.outstanding.count} invoices</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon jobs-icon">#</div>
          <div className="stat-content">
            <span className="stat-label">Active Jobs</span>
            <span className="stat-value">{data.activeJobs}</span>
            <span className="stat-sub">In progress</span>
          </div>
        </div>
      </div>

      {/* Year to Date Summary */}
      <div className="ytd-summary">
        <h2>Year to Date</h2>
        <div className="ytd-grid">
          <div className="ytd-item">
            <span className="ytd-label">Revenue</span>
            <span className="ytd-value positive">{formatCurrency(data.yearToDate.revenue)}</span>
          </div>
          <div className="ytd-item">
            <span className="ytd-label">Expenses</span>
            <span className="ytd-value negative">{formatCurrency(data.yearToDate.expenses)}</span>
          </div>
          <div className="ytd-item ytd-highlight">
            <span className="ytd-label">Net Profit</span>
            <span className={`ytd-value ${data.yearToDate.profit >= 0 ? 'positive' : 'negative'}`}>
              {formatCurrency(data.yearToDate.profit)}
            </span>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions">
        <h2>Quick Actions</h2>
        <div className="actions-grid">
          <Link to="/estimates/new" className="action-card">
            <span className="action-icon">📝</span>
            <span className="action-label">Create Estimate</span>
          </Link>
          <Link to="/invoices/new" className="action-card">
            <span className="action-icon">📄</span>
            <span className="action-label">Create Invoice</span>
          </Link>
          <Link to="/expenses/new" className="action-card">
            <span className="action-icon">💳</span>
            <span className="action-label">Add Expense</span>
          </Link>
          <Link to="/receipts/scan" className="action-card">
            <span className="action-icon">📷</span>
            <span className="action-label">Scan Receipt</span>
          </Link>
          <Link to="/mileage/new" className="action-card">
            <span className="action-icon">🚗</span>
            <span className="action-label">Log Mileage</span>
          </Link>
          <Link to="/customers/new" className="action-card">
            <span className="action-icon">👤</span>
            <span className="action-label">Add Customer</span>
          </Link>
        </div>
      </div>

      {/* Alerts */}
      {data.unreviewedReceipts > 0 && (
        <div className="alerts-section">
          <div className="alert alert-warning">
            <span className="alert-icon">📋</span>
            <span className="alert-text">
              You have {data.unreviewedReceipts} unreviewed receipt{data.unreviewedReceipts !== 1 ? 's' : ''}.
            </span>
            <Link to="/receipts" className="alert-action">Review Now</Link>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
